/**
 * TOSS Web Application - Notification Types
 * TypeScript types matching .NET 9 backend Notification entity
 * Based on PROJECT_DOCUMENTATION.md Section 19.8
 */

import { NotificationType } from './enums';

/**
 * Main Notification entity
 * Corresponds to Notification class in .NET backend
 */
export interface Notification {
  id: string;
  userId: string;
  /** Full name of the notification recipient — populated by backend since 2026-05-28 */
  userName?: string | null;
  title: string;
  content: string;
  type: NotificationType;
  isRead: boolean;
  /**
   * Priority sector — the red count on the bell (24.09.2026). Four events only, decided by the
   * backend where each is raised; optional because an older API build does not send it.
   */
  isPriority?: boolean;
  applicationId?: string;
  taskId?: string;
  expenseId?: string;
  chatId?: string;
  createdAt: string; // ISO date string
}

/**
 * Notification with related entity details
 */
export interface NotificationFullData extends Notification {
  application?: {
    id: string;
    applicationNumber: string;
  };
  task?: {
    id: string;
    taskName: string;
  };
  expense?: {
    id: string;
    category: string;
    amount: number;
  };
}

/**
 * Unread notifications count response
 */
export interface UnreadNotificationsCount {
  /** Every unread notification — the «Непрочитанные» chip. */
  count: number;
  /** Unread PRIORITY notifications, a subset of `count` — the red number on the bell. */
  priorityCount: number;
}

// ============================================================================
// Request DTOs for Notification API
// ============================================================================

/**
 * Request to mark notification as read
 */
export interface MarkNotificationReadRequest {
  isRead: boolean;
}

// ============================================================================
// Filter types for Notification queries
// ============================================================================

/**
 * Filters for notification list queries
 */
export interface NotificationFilters {
  types?: NotificationType[];
  isRead?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// SignalR Real-time Types
// ============================================================================

/**
 * Real-time notification event from SignalR
 */
export interface NotificationEvent {
  notification: Notification;
  eventType: 'created' | 'updated' | 'deleted';
}

/**
 * Real-time application update event from SignalR
 */
export interface ApplicationUpdateEvent {
  applicationId: string;
  status: string;
  eventType: 'created' | 'updated' | 'statusChanged';
}

/**
 * Real-time task update event from SignalR
 */
export interface TaskUpdateEvent {
  taskId: string;
  status: string;
  eventType: 'created' | 'updated' | 'statusChanged';
}

/**
 * Real-time expense update event from SignalR
 */
export interface ExpenseUpdateEvent {
  expenseId: string;
  status: string;
  eventType: 'created' | 'updated' | 'statusChanged';
}

/**
 * SignalR hub connection state
 */
export type SignalRConnectionState =
  | 'Disconnected'
  | 'Connecting'
  | 'Connected'
  | 'Disconnecting'
  | 'Reconnecting';

/**
 * Force logout event from SignalR.
 *
 * Sent when the server ends this account's sessions: it was blocked or deleted, its login
 * e-mail was changed, or an administrator changed its role / ПТО and the access token's claims
 * went stale (`ACCESS_CHANGED`). `reason` stays a bare `string` rather than a union on purpose —
 * the server may ship a code older builds do not know, and `getForceLogoutMessage` in
 * `realtime/useNotifications.ts` answers an unknown one neutrally instead of guessing.
 */
export interface ForceLogoutEvent {
  reason: string; // 'USER_BLOCKED' | 'USER_DELETED' | 'CREDENTIALS_CHANGED' | 'ACCESS_CHANGED'
}

/**
 * SignalR hub method names for notifications
 *
 * Note: User group membership is automatically managed by the server
 * in OnConnectedAsync/OnDisconnectedAsync based on Context.UserIdentifier.
 * No explicit JoinGroup/LeaveGroup calls are needed for user notifications.
 */
export const NotificationHubMethods = {
  // Server-to-client methods
  //
  // JoinRoleGroup / LeaveRoleGroup were removed 2026-08-18 together with the server methods:
  // nothing ever called them, no server code ever sent to a role_* group, and the hub methods
  // joined an arbitrary group without checking the caller was actually in that role.
  ReceiveNotification: 'ReceiveNotification',
  ReceiveApplicationUpdate: 'ReceiveApplicationUpdate',
  ReceiveTaskUpdate: 'ReceiveTaskUpdate',
  ReceiveExpenseUpdate: 'ReceiveExpenseUpdate',
  ForceLogout: 'ForceLogout',
  /**
   * The company a portal session acts for changed — either the customer used the header
   * switcher, or an administrator revoked the membership it was acting on (client portal
   * Р4, decision Р20). Payload is the new `customerId` (a bare string, not an object).
   * See `hooks/api/clientPortal/useClientSessionRealtime.ts`.
   */
  CompanySwitched: 'CompanySwitched',
} as const;

/**
 * SignalR group names for real-time subscriptions
 * Note: User groups use underscore format (user_) matching server convention
 */
export const NotificationGroups = {
  User: (userId: string) => `user_${userId}`,
  // Role: removed 2026-08-18 — role_* groups never existed server-side. Fan-out to a set of
  // roles is done by resolving the users and sending to their personal groups; see
  // MechanicNotificationService.NotifyUnclaimedPtoAsync for the working pattern.
  Application: (applicationId: string) => `application_${applicationId}`,
  PTO: (ptoId: string) => `pto_${ptoId}`,
  AllUsers: 'all-users',
} as const;
